import React, { useState, useEffect, useCallback, useRef } from "react";
import "../ExitSnapshot/ExitSnapshotView.css";
import "../ExitSnapshot/B10CEDetailView.css";
import { fetchEntrySnapshotAtTime, fetchEntrySnapshotMeta, fetchEntryAvailableTimes, fetchEntryDateList, fetchEntryClassDetail, fetchEntryTriggerCounts } from "../../services/entrySnapshotService";
import B10CEDetailView from "./EntryClassDetailView";

// ─── Constants ─────────────────────────────────────────────────────────────

const OPTION_TYPES = ["CE", "PE"];

// ─── Utility helpers ───────────────────────────────────────────────────────

function formatValue(value) {
  if (value === null || value === undefined) return { text: "\u2014", cls: "es-metric-value--null" };
  if (typeof value === "boolean") {
    return { text: value ? "TRUE" : "FALSE", cls: value ? "es-metric-value--true" : "es-metric-value--false" };
  }
  if (typeof value === "number") {
    const formatted = Number.isInteger(value) ? String(value) : value.toFixed(2);
    return { text: formatted, cls: "es-metric-value--number" };
  }
  if (typeof value === "string") return { text: value, cls: "es-metric-value--string" };
  return { text: JSON.stringify(value), cls: "es-metric-value--string" };
}

function todayIST() {
  const now = new Date();
  const ist = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return ist.toISOString().slice(0, 10);
}

// ─── Sub-components ────────────────────────────────────────────────────────

function MetricRow({ name, value }) {
  const { text, cls } = formatValue(value);
  const isTrue = value === true;
  return (
    <div className={`es-metric-row${isTrue ? " es-metric-row--true" : ""}`}>
      <span className="es-metric-key" title={name}>{name}</span>
      <span className={`es-metric-value ${cls}`}>{text}</span>
    </div>
  );
}

function MetricGroup({ name, fields }) {
  return (
    <div className="es-metric-group">
      <div className="es-metric-group__header">{name}</div>
      {Object.entries(fields).map(([key, val]) => (
        <MetricRow key={key} name={key} value={val} />
      ))}
    </div>
  );
}

function MatchedExitsList({ matchedExits }) {
  if (!matchedExits || matchedExits.length === 0) return null;
  return (
    <div className="es-matched-exits">
      <div className="es-matched-exits__title">Matched Entries ({matchedExits.length})</div>
      <div>
        {matchedExits.map((me, i) => (
          <div className="es-exit-chip" key={i} title={me.exitInfo || ""}>
            <span className="es-exit-chip__code">{me.exitCode || me.exitClassName}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EntryClassList({ exitClasses, selectedClass, onSelect, mode }) {
  const renderClassItem = (cls) => (
    <div
      key={cls.className}
      className={[
        "es-class-item",
        cls.matched ? "es-class-item--matched" : "",
        selectedClass === cls.className ? "es-class-item--selected" : "",
      ].join(" ")}
      onClick={() => onSelect(cls.className)}
    >
      <span className={`es-class-badge ${cls.matched ? "es-class-badge--matched" : "es-class-badge--unmatched"}`}>
        {cls.matched ? "\u2713" : "\u2717"}
      </span>
      <span className="es-class-name">{cls.className}</span>
      {cls.matchedExitCount > 0 && (
        <span className="es-class-count">{cls.matchedExitCount}</span>
      )}
    </div>
  );

  return (
    <div className="es-class-list">
      <div className="es-class-list__header">
        {mode === "lastHour" ? "LAST HOUR ENTRIES" : "FIRST 45 MIN ENTRIES"}
      </div>
      <div
        className={`es-class-item${!selectedClass ? " es-class-item--selected" : ""}`}
        onClick={() => onSelect(null)}
      >
        <span className="es-class-badge es-class-badge--unmatched">&sum;</span>
        <span className="es-class-name" style={{ color: "#c9d1d9" }}>All Metrics</span>
      </div>
      {exitClasses.map(renderClassItem)}
    </div>
  );
}

function DetailHeader({ selectedClassData, summary, source }) {
  const label = selectedClassData ? selectedClassData.className : "All Entry Metrics";
  const matched = selectedClassData ? selectedClassData.matched : false;
  return (
    <div className="es-detail-header">
      <span className={`es-detail-header__code${matched ? " es-detail-header__code--matched" : ""}`}>
        {label}
      </span>
      {summary && (
        <>
          <span className="es-detail-header__stat">
            Matched: <span className="es-detail-header__stat-val">{summary.matchedCount}</span>
          </span>
          <span className="es-detail-header__stat">
            Classes: <span className="es-detail-header__stat-val">{summary.matchedClasses}/{summary.totalClasses}</span>
          </span>
          {summary.expiry && (
            <span className="es-detail-header__stat">
              Expiry: <span className="es-detail-header__stat-val">{summary.expiry}</span>
            </span>
          )}
          {summary.indexOpeningType && (
            <span className="es-detail-header__stat">
              Opening: <span className="es-detail-header__stat-val">{summary.indexOpeningType}</span>
            </span>
          )}
        </>
      )}
      {source && (
        <span className="es-source-badge es-source-badge--exit_items">
          from {source}
        </span>
      )}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────

export default function EntrySnapshotView({ mode }) {
  const [optionType, setOptionType] = useState("CE");
  const [onlyDate, setOnlyDate]     = useState(todayIST());
  const [onlyTime, setOnlyTime]     = useState("09:20");

  const indexName = "NIFTY 50";
  const entryMode = mode || "first45";

  const [dateList, setDateList]           = useState([]);
  const [dateListLoading, setDateListLoading] = useState(false);
  const [meta, setMeta]                     = useState({ expiries: [], dates: [] });
  const [availableTimes, setAvailableTimes] = useState([]);
  const [snapshotData, setSnapshotData]     = useState(null);
  const [selectedClass, setSelectedClass]   = useState(null);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [initialLoad, setInitialLoad]       = useState(true);

  const loadSnapshotRef = useRef(null);

  // ── Fetch snapshot ────────────────────────────────────
  const doLoad = useCallback((opt, date, time) => {
    if (!opt || !date || !time) return;
    setLoading(true);
    setError(null);
    fetchEntrySnapshotAtTime({ indexName, optionType: opt, onlyDate: date, onlyTime: time, mode: entryMode })
      .then((data) => {
        if (data?.error) {
          setSnapshotData(null);
          setError(data.error);
        } else {
          setSnapshotData(data);
          setError(null);
        }
      })
      .catch((e) => {
        setError(e?.response?.data || e.message || "Failed to load snapshot");
        setSnapshotData(null);
      })
      .finally(() => setLoading(false));
  }, [entryMode]);

  const loadSnapshot = useCallback(() => {
    doLoad(optionType, onlyDate, onlyTime);
  }, [doLoad, optionType, onlyDate, onlyTime]);

  loadSnapshotRef.current = loadSnapshot;

  // ── Fetch date list ──────────────────────────────────
  useEffect(() => {
    setDateListLoading(true);
    fetchEntryDateList({ indexName, mode: entryMode, optionType })
      .then((d) => {
        const dates = d?.dates || [];
        setDateList(dates);
        if (dates.length > 0 && !dates.includes(onlyDate)) {
          setOnlyDate(dates[0]);
        }
      })
      .catch(() => setDateList([]))
      .finally(() => setDateListLoading(false));
  }, [entryMode, optionType]); // eslint-disable-line

  // ── Load meta on filter change ─────────────────────
  useEffect(() => {
    setSnapshotData(null);
    setSelectedClass(null);
    fetchEntrySnapshotMeta({ indexName, optionType })
      .then((m) => setMeta(m || { expiries: [], dates: [] }))
      .catch(() => setMeta({ expiries: [], dates: [] }));
  }, [optionType]);

  // ── Load available times when date changes ──────────
  useEffect(() => {
    if (!onlyDate) return;
    fetchEntryAvailableTimes({ indexName, optionType, onlyDate, mode: entryMode })
      .then((t) => {
        const times = t?.times || [];
        setAvailableTimes(times);
        if (times.length > 0) {
          const firstTime = times[0].slice(0, 5);
          setOnlyTime(firstTime);
          doLoad(optionType, onlyDate, firstTime);
        } else {
          setSnapshotData(null);
          setError(null);
        }
        setInitialLoad(false);
      })
      .catch(() => {
        setAvailableTimes([]);
        setInitialLoad(false);
      });
  }, [optionType, onlyDate, entryMode, doLoad]);

  // ── Date step ──────────────────────────────────────
  const stepDate = useCallback((delta) => {
    if (dateList.length === 0) return;
    const idx = dateList.indexOf(onlyDate);
    const cur = idx >= 0 ? idx : 0;
    const next = Math.max(0, Math.min(dateList.length - 1, cur - delta));
    setOnlyDate(dateList[next]);
  }, [dateList, onlyDate]);

  // ── Time navigation ───────────────────────────────────
  const stepTime = useCallback((delta) => {
    if (availableTimes.length === 0) return;
    const idx = availableTimes.findIndex((t) => t.startsWith(onlyTime));
    const cur = idx >= 0 ? idx : 0;
    const next = Math.max(0, Math.min(availableTimes.length - 1, cur + delta));
    const newTime = availableTimes[next].slice(0, 5);
    setOnlyTime(newTime);
    doLoad(optionType, onlyDate, newTime);
  }, [availableTimes, onlyTime, doLoad, optionType, onlyDate]);

  // ── Derived state ────────────────────────────────────
  const exitClasses    = snapshotData?.exitClasses || [];
  const groupedMetrics = snapshotData?.exitRuleDataGrouped || {};
  const summary        = snapshotData?.summary || null;
  const source         = snapshotData?.source || null;

  const selectedClassData = selectedClass
    ? exitClasses.find((c) => c.className === selectedClass)
    : null;

  const matchedExitsToShow = selectedClassData?.matchedExits || [];

  return (
    <div className="es-root">

      {/* ── Filter bar ─────────────────────────────────────────── */}
      <div className="es-filters">

        <label>Index</label>
        <span style={{ color: "#58a6ff", fontWeight: 600, padding: "0 8px" }}>NIFTY 50</span>

        <label>Type</label>
        <select value={optionType} onChange={(e) => setOptionType(e.target.value)}>
          {OPTION_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>

        <div className="es-filter-sep" />

        <span className="es-summary-pill" style={{ background: entryMode === "lastHour" ? "#3d2e00" : "#0d4d1f", color: entryMode === "lastHour" ? "#d29922" : "#3fb950" }}>
          {entryMode === "lastHour" ? "Last Hour" : "First 45 Min"}
        </span>

        <div className="es-filter-sep" />

        {/* Date navigation */}
        <div className="es-btn-group">
          <button className="es-btn" onClick={() => stepDate(7)} title="-7 dates">-7</button>
          <button className="es-btn" onClick={() => stepDate(1)} title="-1 date">-1</button>
        </div>
        {dateList.length > 0 ? (
          <select
            className="es-date-select"
            value={onlyDate}
            onChange={(e) => setOnlyDate(e.target.value)}
          >
            {dateList.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        ) : (
          <span className="es-date-display">
            {dateListLoading ? "Loading..." : onlyDate}
          </span>
        )}
        <div className="es-btn-group">
          <button className="es-btn" onClick={() => stepDate(-1)} title="+1 date">+1</button>
          <button className="es-btn" onClick={() => stepDate(-7)} title="+7 dates">+7</button>
        </div>

        <div className="es-filter-sep" />

        {/* Time navigation */}
        <div className="es-btn-group">
          <button className="es-btn" onClick={() => stepTime(-5)}>-5m</button>
          <button className="es-btn" onClick={() => stepTime(-1)}>-1m</button>
        </div>
        <span className="es-time-display">{onlyTime}</span>
        <div className="es-btn-group">
          <button className="es-btn" onClick={() => stepTime(1)}>+1m</button>
          <button className="es-btn" onClick={() => stepTime(5)}>+5m</button>
        </div>

        {availableTimes.length > 0 && (
          <select
            value={availableTimes.find((t) => t.startsWith(onlyTime)) || ""}
            onChange={(e) => {
              const t = e.target.value.slice(0, 5);
              setOnlyTime(t);
              doLoad(optionType, onlyDate, t);
            }}
            style={{ width: 100 }}
          >
            {availableTimes.map((t) => (
              <option key={t} value={t}>{t.slice(0, 5)}</option>
            ))}
          </select>
        )}

        <div className="es-filter-sep" />

        <button className="es-btn es-btn--load" onClick={loadSnapshot}>
          Load
        </button>

        {summary && (
          <>
            <span className="es-summary-pill es-summary-pill--matched">
              {summary.matchedClasses}/{summary.totalClasses} classes matched
            </span>
            {summary.indexOpeningType && (
              <span className="es-summary-pill es-summary-pill--opening">
                {summary.indexOpeningType}
              </span>
            )}
          </>
        )}
      </div>

      {/* ── Body ─────────────────────────────────────────────── */}
      <div className="es-body">

        <EntryClassList
          exitClasses={exitClasses}
          selectedClass={selectedClass}
          onSelect={setSelectedClass}
          mode={entryMode}
        />

        <div className="es-detail">

          {selectedClass ? (
            <B10CEDetailView
              indexName={indexName}
              optionType={optionType}
              onlyDate={onlyDate}
              onlyTime={onlyTime}
              selectedClass={selectedClass}
              mode={entryMode}
              onTimeChange={(t) => {
                setOnlyTime(t);
                doLoad(optionType, onlyDate, t);
              }}
              onDateChange={(d) => {
                setOnlyDate(d);
              }}
            />
          ) : (
            <>
              {loading && (
                <div className="es-loading">
                  <div className="es-spinner" />
                  Loading entry data...
                </div>
              )}

              {!loading && error && (
                <div className="es-no-snapshot">
                  <div style={{ fontSize: 32 }}>No Data</div>
                  <div style={{ color: "#f85149" }}>{error}</div>
                  {availableTimes.length > 0 && (
                    <div style={{ color: "#8b949e", fontSize: 11 }}>
                      Available times: {availableTimes.map(t => t.slice(0,5)).join(", ")}
                    </div>
                  )}
                </div>
              )}

              {!loading && !error && !snapshotData && !initialLoad && (
                <div className="es-empty">
                  <div className="es-empty__icon">Entry Analyzer</div>
                  <div>
                    {dateList.length === 0
                      ? "No entry dates found."
                      : availableTimes.length === 0
                        ? "No entry data for this date."
                        : "Select a time and click Load."
                    }
                  </div>
                </div>
              )}

              {!loading && !error && !snapshotData && initialLoad && (
                <div className="es-loading">
                  <div className="es-spinner" />
                  Initializing...
                </div>
              )}

              {!loading && !error && snapshotData && (
                <>
                  <DetailHeader selectedClassData={selectedClassData} summary={summary} source={source} />

                  {!selectedClass && exitClasses.length > 0 && (
                    <MatchedExitsList
                      matchedExits={exitClasses.flatMap((c) => c.matchedExits || [])}
                    />
                  )}

                  <div className="es-metrics-scroll">
                    {Object.keys(groupedMetrics).length === 0 ? (
                      <div className="es-no-snapshot">
                        <div>No metric data available.</div>
                      </div>
                    ) : (
                      <div className="es-metric-groups">
                        {Object.entries(groupedMetrics).map(([groupName, fields]) => (
                          <MetricGroup key={groupName} name={groupName} fields={fields} />
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
}
